import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Spinner,
  Switch,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Send24Regular } from "@fluentui/react-icons";
import { useData } from "../../data/DataProvider";
import { inviaRichiesta, sceltePrecedenti } from "../../data/richieste";
import { areePer, serviziPerSede, useConfigurazione } from "../../data/configurazione";
import { goBack, replace } from "../../lib/nav";
import { Shell } from "../../components/Shell";
import { Section } from "../../components/Section";
import { ChipGroup } from "../../components/ChipGroup";
import { PhotoPicker } from "../../components/PhotoPicker";
import { describeError } from "../../components/ErrorState";

const useStyles = makeStyles({
  urgente: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  progress: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS, flexGrow: 1 },
});

type Errors = Partial<Record<"sede" | "servizio" | "area" | "categoria" | "titolo" | "descrizione", string>>;

export function NuovaRichiesta() {
  const s = useStyles();
  const data = useData();
  const prev = useMemo(() => sceltePrecedenti(data), [data]);

  const { config, error: configError } = useConfigurazione(data);
  const [sede, setSede] = useState<string | undefined>(prev.sede);
  const [servizio, setServizio] = useState<string | undefined>(prev.servizio);
  const [area, setArea] = useState<string>();
  const [categoria, setCategoria] = useState<string>();
  const [titolo, setTitolo] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [urgente, setUrgente] = useState(false);
  const [foto, setFoto] = useState<File[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState<{ fase: string; frazione: number }>();
  const [sendError, setSendError] = useState<string>();

  useEffect(() => {
    if (configError) setSendError(describeError(configError).detail);
  }, [configError]);

  const servizi = config ? serviziPerSede(config, sede) : undefined;
  const aree = config ? areePer(config, sede, servizio) : [];

  // Le scelte ricordate (o fatte prima di cambiare sede) potrebbero non valere più
  useEffect(() => {
    if (!config) return;
    if (sede && !config.sedi.includes(sede)) setSede(undefined);
    if (servizio && !serviziPerSede(config, sede).includes(servizio)) setServizio(undefined);
    if (area && !areePer(config, sede, servizio).includes(area)) setArea(undefined);
  }, [config, sede, servizio, area]);

  function validate(): boolean {
    const e: Errors = {};
    if (!sede) e.sede = "Scegli la sede.";
    if (!servizio) e.servizio = "Scegli il servizio.";
    if (aree.length && !area) e.area = "Scegli l'area.";
    if (!categoria) e.categoria = "Scegli la categoria.";
    if (!titolo.trim()) e.titolo = "Scrivi un titolo breve.";
    if (!descrizione.trim()) e.descrizione = "Descrivi il problema.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSendError(undefined);
    setSending({ fase: "Invio della richiesta", frazione: 0 });
    try {
      const res = await inviaRichiesta(
        data,
        {
          titolo,
          sede: sede!,
          servizio: servizio!,
          area: aree.length ? area : undefined,
          categoria: categoria!,
          descrizione,
          urgente,
          foto,
        },
        (fase, frazione) => setSending({ fase, frazione }),
      );
      replace(`/inviata/${res.id}?ko=${res.fotoNonCaricate}`);
    } catch (e) {
      setSendError(describeError(e).detail);
      setSending(undefined);
    }
  }

  const clear = (k: keyof Errors) => setErrors((e) => ({ ...e, [k]: undefined }));

  return (
    <Shell
      title="Nuova richiesta"
      onBack={sending ? undefined : goBack}
      footer={
        sending ? (
          <div className={s.progress}>
            <span>{sending.fase}…</span>
            <ProgressBar value={sending.frazione} />
          </div>
        ) : (
          <Button appearance="primary" size="large" icon={<Send24Regular />} style={{ flexGrow: 1 }} onClick={submit}>
            Invia richiesta
          </Button>
        )
      }
    >
      {sendError && (
        <MessageBar intent="error">
          <MessageBarBody>{sendError}</MessageBarBody>
        </MessageBar>
      )}

      <Section title="Dove">
        <Field label="Sede" required validationMessage={errors.sede}>
          {config ? (
            <ChipGroup
              ariaLabel="Sede"
              options={config.sedi}
              value={sede}
              onChange={(v) => {
                setSede(v);
                clear("sede");
              }}
            />
          ) : (
            <Spinner size="tiny" />
          )}
        </Field>
        {sede && servizi && (
          <Field label="Servizio" required validationMessage={errors.servizio}>
            <ChipGroup
              ariaLabel="Servizio"
              options={servizi}
              value={servizio}
              onChange={(v) => {
                setServizio(v);
                clear("servizio");
              }}
            />
          </Field>
        )}
        {aree.length > 0 && (
          <Field label="Area" required validationMessage={errors.area}>
            <ChipGroup
              ariaLabel="Area"
              options={aree}
              value={area}
              onChange={(v) => {
                setArea(v);
                clear("area");
              }}
            />
          </Field>
        )}
      </Section>

      <Section title="Cosa">
        <Field label="Categoria" required validationMessage={errors.categoria}>
          {config ? (
            <ChipGroup
              ariaLabel="Categoria"
              options={config.categorie}
              value={categoria}
              onChange={(v) => {
                setCategoria(v);
                clear("categoria");
              }}
            />
          ) : (
            <Spinner size="tiny" />
          )}
        </Field>
        <Field label="Titolo" required validationMessage={errors.titolo}>
          <Input
            size="large"
            value={titolo}
            maxLength={255}
            placeholder="Tapparella della cucina rotta"
            onChange={(_, d) => {
              setTitolo(d.value);
              clear("titolo");
            }}
          />
        </Field>
        <Field label="Descrizione" required validationMessage={errors.descrizione}>
          <Textarea
            size="large"
            rows={4}
            resize="vertical"
            value={descrizione}
            placeholder="Cosa è successo, dove si trova esattamente, da quando"
            onChange={(_, d) => {
              setDescrizione(d.value);
              clear("descrizione");
            }}
          />
        </Field>
        <div className={s.urgente}>
          <div>
            <div>Urgente</div>
            <div className={s.hint}>Rischi per la sicurezza o servizio bloccato</div>
          </div>
          <Switch checked={urgente} onChange={(_, d) => setUrgente(d.checked)} aria-label="Urgente" />
        </div>
      </Section>

      <Section title="Foto">
        <PhotoPicker files={foto} onChange={setFoto} />
      </Section>
    </Shell>
  );
}
